/**
 * Cross-domain config:
 * - VITE_APP_URL       → the authenticated-app origin (e.g. https://app.unfoldwin.com)
 * - VITE_MARKETING_URL → the marketing origin         (e.g. https://unfoldwin.com)
 *
 * In dev (and any single-origin deploy) leave both unset and we fall back to
 * window.location.origin, so the existing same-origin flow still works.
 */

function trimSlashes(value: string): string {
  return value.replace(/\/+$/, "");
}

/**
 * Production hostname fallbacks — used when VITE_APP_URL / VITE_MARKETING_URL
 * aren't inlined into the build (Vite reads `VITE_*` at build time, so a Vercel
 * env-var change without a redeploy wouldn't take effect). Detecting the
 * hostname at runtime makes the split-domain redirects robust.
 */
const RUNTIME_FALLBACKS: Record<"VITE_APP_URL" | "VITE_MARKETING_URL", { hosts: string[]; url: string }> = {
  VITE_APP_URL: {
    hosts: ["app.unfoldwin.com", "unfoldwin.com", "www.unfoldwin.com"],
    url: "https://app.unfoldwin.com",
  },
  VITE_MARKETING_URL: {
    hosts: ["app.unfoldwin.com", "unfoldwin.com", "www.unfoldwin.com"],
    url: "https://unfoldwin.com",
  },
};

function readEnvUrl(key: "VITE_APP_URL" | "VITE_MARKETING_URL"): string {
  const raw = import.meta.env[key];
  if (typeof raw === "string" && raw.trim()) return trimSlashes(raw.trim());
  if (typeof window !== "undefined") {
    const fallback = RUNTIME_FALLBACKS[key];
    if (fallback && fallback.hosts.includes(window.location.hostname)) {
      return fallback.url;
    }
  }
  return "";
}

function getOriginBase(): string {
  if (typeof window === "undefined") return "";
  const base = import.meta.env.BASE_URL?.replace(/\/+$/, "") || "";
  return `${window.location.origin}${base}`;
}

/** Origin of the authenticated app (subdomain in prod, current origin in dev). */
export function getAppUrl(): string {
  return readEnvUrl("VITE_APP_URL") || getOriginBase();
}

/** Origin of the marketing site (apex domain in prod, current origin in dev). */
export function getMarketingUrl(): string {
  return readEnvUrl("VITE_MARKETING_URL") || getOriginBase();
}

/**
 * True when both VITE_APP_URL and VITE_MARKETING_URL are configured to
 * different origins — i.e. we're in a real split-domain deployment and
 * cross-domain redirects should kick in.
 */
export function isCrossDomainConfigured(): boolean {
  const app = readEnvUrl("VITE_APP_URL");
  const marketing = readEnvUrl("VITE_MARKETING_URL");
  return Boolean(app && marketing && app !== marketing);
}

export function isOnAppDomain(): boolean {
  if (typeof window === "undefined") return false;
  const app = readEnvUrl("VITE_APP_URL");
  if (!app) return true;
  try {
    return new URL(app).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Marketing = "not the app domain". This deliberately swallows both the apex
 * (unfoldwin.com) and www. variant, plus any vercel preview hosts in the
 * single-origin / dev case. Without this, www.unfoldwin.com would silently
 * fail the marketing-bouncer check because its origin doesn't equal
 * VITE_MARKETING_URL exactly.
 */
export function isOnMarketingDomain(): boolean {
  if (typeof window === "undefined") return false;
  return !isOnAppDomain();
}

/**
 * URL of the in-app login page. From the marketing domain we send users to
 * the app domain so the resulting session is stored where it's actually
 * needed (localStorage is origin-scoped).
 */
export function getLoginUrl(): string {
  if (isCrossDomainConfigured() && !isOnAppDomain()) {
    return `${getAppUrl()}/login`;
  }
  return "/login";
}
